// src/ConveGroupInput.tsx

import React, { useState, useRef, useCallback, useEffect } from 'react';

interface Props {
  onSend: (content: string, mentionAi: boolean) => void;
  onTyping: () => void;
  disabled?: boolean;
}

const ConveGroupInput: React.FC<Props> = ({ onSend, onTyping, disabled }) => {
  const [input, setInput] = useState('');
  const [showAiPopup, setShowAiPopup] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInput(value);

    // @ai 자동완성 팝업
    const atIdx = value.lastIndexOf('@');
    if (atIdx >= 0) {
      const afterAt = value.slice(atIdx + 1).toLowerCase();
      setShowAiPopup('ai'.startsWith(afterAt) && afterAt.length <= 2);
    } else {
      setShowAiPopup(false);
    }

    // 타이핑 이벤트 디바운스 (300ms)
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      onTyping();
    }, 300);
  };

  const handleSelectAi = () => {
    const atIdx = input.lastIndexOf('@');
    const newInput = input.slice(0, atIdx) + '@ai ';
    setInput(newInput);
    setShowAiPopup(false);
    textareaRef.current?.focus();
  };

  const detectMentionAi = (text: string): boolean => {
    return /@ai\b/i.test(text);
  };

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || disabled) return;
    const mentionAi = detectMentionAi(trimmed);
    onSend(trimmed, mentionAi);
    setInput('');
    setShowAiPopup(false);
  }, [input, disabled, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // textarea 높이 자동 조절
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
    }
  }, [input]);

  return (
    <div className="conve-group-input-wrap">
      {showAiPopup && (
        <div className="conve-ai-popup" onClick={handleSelectAi}>
          <span className="conve-ai-popup-icon">🤖</span>
          <span>@ai - AI에게 질문하기</span>
        </div>
      )}
      <div className="conve-group-input-row">
        <textarea
          ref={textareaRef}
          className="conve-group-textarea"
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="메시지를 입력하세요... (@ai로 AI 호출)"
          disabled={disabled}
          rows={1}
        />
        <button
          className="conve-group-send-btn"
          onClick={handleSend}
          disabled={disabled || !input.trim()}
        >
          ↑
        </button>
      </div>
    </div>
  );
};

export default ConveGroupInput;
